import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { SessionState } from 'sip.js';
import * as lib from '~/dependencies';
import messagesToast from '~/validations/messagesNotifications';
import * as actions from '~/store/actions';
import * as actionTypes from '~/store/actions/actionTypes';
import { useCallHold, useDetectMicrophone } from '~/hooks';
import {
  log,
  isValidInviteTarget,
  sdpModifierCodec,
  buildDeviceInfoHeader,
  resolveAudioConstraints,
  conferenceMixer,
} from '~/helpers';
import { IExtra, ActionControl } from '~/interfaces';
import useTypedSelector from './useTypedSelector';

export default function useControlSessions() {
  // HOOKS
  const dispatch = useDispatch();
  const history = useHistory();
  const microphone = useDetectMicrophone();
  const setCallHold = useCallHold(false);

  // SELECTORS
  const sessionActive = useTypedSelector(state => state.callCurrent.session);
  const sessions = useTypedSelector(state => state.callCurrent.data);
  const ua = useTypedSelector(state => state.phone.ua);
  const statusSystem = useTypedSelector(state => state.system?.status);
  const microphoneDevice = useTypedSelector(
    state => state.system?.deviceManager?.audio?.microphone
  );
  const microphonePin = useTypedSelector(
    state => state.system?.userPreferences?.audio?.microphone
  );

  //  General function to control functions of system voip
  const controlSessions = useCallback(
    async (
      id: string | null = null,
      action: ActionControl,
      value: string | number = '',
      extra: IExtra | null = null
    ) => {
      const { session, id: sessionId, status: sessionStatus } = {
        ...sessions?.find(ses => ses.id === id),
      };

      switch (action) {
        case 'invite':
          if (ua?.isRegistered() && isValidInviteTarget(value)) {
            // The pinned device when the user chose one and it is STILL attached, otherwise the
            // OS-default aggregate. Verifying against the live enumeration is what keeps a
            // detached device out of the request in the first place.
            const {
              audio: preferred,
              fallbacks,
              usedFallback: pinDetached,
            } = await resolveAudioConstraints(microphonePin, microphoneDevice);

            // The probe still runs, for the cases enumeration cannot answer: no microphone at
            // all, permission revoked, or the preferred device attached but held exclusively by
            // another application. It walks the same ladder the media factory would and returns
            // whichever constraint actually opened, so the INVITE is not sent at all when there is
            // nothing to send it with.
            // @ts-ignore
            const audio = await microphone(preferred, fallbacks);

            try {
              if (audio) {
                // The probe opened something other than the preferred constraint, so the resolved
                // device in redux is no longer what we are about to use: re-resolve rather than
                // advertise the wrong device in the SIP header.
                const usedFallback = pinDetached || audio !== preferred;

                if (usedFallback) {
                  dispatch(actions.phoneDeviceSetup());
                }

                const deviceHeader = usedFallback
                  ? undefined
                  : buildDeviceInfoHeader(microphoneDevice?.label);
                const newSession = ua.makeCall(value, {
                  sessionDescriptionHandlerOptions: {
                    constraints: {
                      audio,
                      video: false,
                    },
                    iceGatheringTimeout: 700,
                  },
                  sessionDescriptionHandlerModifiers: [sdpModifierCodec],
                  ...(deviceHeader ? { extraHeaders: [deviceHeader] } : {}),
                });

                if (extra?.name || extra?.contactId) {
                  dispatch(
                    actions.addNewSession(newSession, {
                      name: extra.name,
                      contactId: extra.contactId,
                      invite: true,
                    })
                  );
                } else if (extra?.waitingToMergeConf) {
                  dispatch(
                    actions.addNewSession(newSession, {
                      waitingToMergeConf: extra.waitingToMergeConf,
                      invite: true,
                    })
                  );
                } else if (extra?.transferOff) {
                  dispatch(actions.updateCall('transferOff', id));
                  dispatch(actions.addNewSession(newSession, { invite: true }));
                } else {
                  dispatch(actions.addNewSession(newSession, { invite: true }));
                }

                if (history?.location?.pathname === '/app/dialPad') {
                  setTimeout(() => {
                    history.push('/app/displayCall');
                  });
                }
              } else {
                lib.ToastMessage(
                  'warn',
                  messagesToast().SYSTEM.microphone.error
                );
              }

              // Clean number from dial after call started
              dispatch(actions.numberToTransfer());
            } catch (err) {
              log.error('Failed to execute UA.makeCall.', err);
            }
          } else if (
            !ua?.isRegistered() &&
            statusSystem !== actionTypes.STATUS.BUSY
          ) {
            // Set Unavailable while trying to register
            dispatch(actions.statusSystem('Unavailable'));
          } else {
            lib.ToastMessage(
              'warn',
              `${messagesToast().NOTIFICATIONS.call.invalidPhoneNumber}`
            );
          }
          break;
        case 'acceptOutgoing':
          dispatch(actions.updateCall(action, sessionId));
          break;
        case 'accept':
          if (sessionActive?.state === SessionState.Established) {
            setCallHold(sessionActive, true);
            dispatch(actions.updateCall('hold', sessionActive?.id));
          }
          dispatch(actions.updateCall(action, sessionId));
          break;
        case 'finish':
          if (session) {
            if (sessionStatus === actionTypes.CALL_CONFERENCE) {
              // Restore the microphone tracks and close the mixing graph before hanging
              // up, otherwise the mixed tracks stay live after both legs are gone.
              await conferenceMixer.destroy();

              sessions.forEach(e => {
                dispatch(actions.updateCall(action, e.id));
              });
            } else {
              dispatch(actions.updateCall(action, sessionId));
            }
          }
          break;
        case 'cancel':
          if (session) {
            dispatch(actions.updateCall(action, sessionId));
          }
          break;
        case 'reject':
          dispatch(actions.PlayRing('endTone', true));
          dispatch(actions.updateCall(action, sessionId));
          break;
        case 'reinvite':
          dispatch(actions.updateCall(action, sessionId));
          controlSessions(sessionId, 'unhold');
          break;
        case 'blindtransfer':
          dispatch(actions.updateCall(action, sessionId, value));
          break;
        case 'attendedtransfer':
          dispatch(actions.updateCall(action, sessionId));
          break;
        case 'mute':
          dispatch(actions.updateCall(action, sessionId));
          break;
        case 'unmute':
          dispatch(actions.updateCall(action, sessionId));
          break;
        case 'transferOn':
          controlSessions(sessionId, 'hold');
          dispatch(actions.updateCall(action, sessionId));
          break;
        case 'transferOff':
          dispatch(actions.updateCall(action, sessionId));
          break;
        case 'waitingTransfer':
          dispatch(actions.updateCall(action, sessionId, value));
          break;
        case 'dtmfAction':
          if (!value) {
            dispatch(actions.updateCall('dtmf', sessionId, ''));
          }
          dispatch(actions.updateCall(action, sessionId, value));
          break;
        case 'hold':
          if (session) {
            setCallHold(session, true);
          }
          break;
        case 'unhold':
          if (session) {
            setCallHold(session, false);
          }
          break;
        case 'clean':
          dispatch(
            actions.callsCurrent(sessions.filter(s => s.finished !== true))
          );
          break;
        case 'dtmf':
          try {
            const dtmfValue = ua.formatDtmfCode(value);
            if (action) {
              if (dtmfValue.slice(-1) && !extra?.erase) {
                if (extra?.lastDigit) {
                  const lastDigit = ua.formatDtmfCode(extra.lastDigit);
                  ua.sendDtmf(lastDigit, session);
                } else {
                  ua.sendDtmf(dtmfValue.slice(-1), session);
                }
              }
              dispatch(actions.updateCall(action, sessionId, dtmfValue));
            } else {
              dispatch(actions.updateCall(action, sessionId, ''));
            }
          } catch (err) {
            log.info('Failed to send DTMF', err);
          }
          break;
        case 'dtmfDigit':
          try {
            const dtmfValue = ua.formatDtmfCode(value);

            if (extra?.lastDigit) {
              const lastDigit = ua.formatDtmfCode(extra.lastDigit);
              ua.sendDtmf(lastDigit, session);
              dispatch(actions.updateCall('dtmf', sessionId, dtmfValue));
            }
          } catch (err) {
            log.error('Failed to send DTMF', err);
          }
          break;
        case 'conferenceOn':
          dispatch(actions.updateCall(action, sessionId, ''));
          break;
        case 'conferenceOff':
          dispatch(actions.updateCall(action, sessionId, ''));
          break;
        case 'announcementOn':
          dispatch(actions.updateCall(action, sessionId, ''));
          break;
        case 'tagsOff':
          dispatch(actions.updateCall(action, sessionId, ''));
          break;
        case 'tagsOn':
          dispatch(actions.updateCall(action, sessionId, ''));
          break;
        case 'notesOff':
          dispatch(actions.updateCall(action, sessionId, ''));
          break;
        case 'notesOn':
          dispatch(actions.updateCall(action, sessionId, ''));
          break;
        case 'announcementOff':
          dispatch(actions.updateCall(action, sessionId, ''));
          break;
        case 'mergeCallConference':
          try {
            const callIdPeer2 = sessions.findIndex(
              e => e.waitingToMergeConf === true
            );
            const callConfSession = session;
            const callConfSession2 = sessions[callIdPeer2]?.session;

            dispatch(actions.updateCall(action, sessionId, ''));

            if (callConfSession && callConfSession2) {
              const pcSession1: RTCPeerConnection =
                // @ts-ignore
                callConfSession.sessionDescriptionHandler?.peerConnection;
              const pcSession2: RTCPeerConnection =
                // @ts-ignore
                callConfSession2.sessionDescriptionHandler?.peerConnection;

              const mixer = await conferenceMixer.create(
                pcSession1,
                pcSession2
              );

              if (mixer) {
                dispatch(actions.remoteVideo(mixer));
              }
            }
          } catch (err) {
            log.error(`Failed to merge calls: `, err);
          }
          break;
        case 'splitCallConference':
          try {
            const callIdPeer2 = sessions.findIndex(
              e => e.waitingToMergeConf === true
            );

            // Get peer sessions
            const callConfSession2 = sessions[callIdPeer2]?.session;

            if (callConfSession2) {
              const pcSession2: RTCPeerConnection =
                // @ts-ignore
                callConfSession2.sessionDescriptionHandler?.peerConnection;

              // Stop mixing first: both legs are still sending the mixed track, so
              // without this the participants keep hearing each other after the split.
              await conferenceMixer.destroy();

              const mediaStreamSession2 = new MediaStream();

              // When the coonference is splitted, set the second call as active.
              pcSession2.getReceivers().forEach(receiver => {
                mediaStreamSession2.addTrack(receiver.track);
              });

              dispatch(actions.remoteVideo(mediaStreamSession2));

              if (session) {
                setCallHold(session, true);

                setTimeout(() => {
                  dispatch(actions.updateCall(action, sessionId, ''));
                  // @ts-ignore
                  clearTimeout(this);
                }, 1000);
              }
            }
          } catch (err) {
            log.error(`Failure to split call conference.`, err);
          }
          break;
        default:
          break;
      }
    },
    [
      sessions,
      microphone,
      ua,
      statusSystem,
      dispatch,
      setCallHold,
      sessionActive,
      microphoneDevice,
      microphonePin,
      history,
    ]
  );

  return controlSessions;
}
